import { CSSProperties } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export type StoryExecutionStatus =
  | 'TODO'
  | 'RUNNING'
  | 'PR_READY'
  | 'CI_FIXING'
  | 'BLOCKED'
  | 'DONE';

export type TimingStatus =
  | 'ON_TRACK'
  | 'LATE'
  | 'RUNNING_LATE'
  | 'COMPLETED_ON_TIME'
  | 'COMPLETED_LATE';

export type HealthStatus = 'GREEN' | 'AMBER' | 'RED';

export interface StoryWithExecution {
  id:              string;
  title:           string;
  externalId:      string | null;
  status:          string;
  priority:        string | null;
  type:            string;
  storyPoints:     number | null;
  executionStatus: StoryExecutionStatus;
  prNumber:        number | null;
  prUrl:           string | null;
  lastEventType:   string | null;
  updatedAt:       string;
  timingStatus:    TimingStatus | null;
  expectedStartDay: number | null;
  expectedEndDay:   number | null;
  currentDay:       number | null;
  daysLate:         number | null;
}

export interface SprintSummary {
  totalStories:    number;
  onTrack:         number;
  late:            number;
  runningLate:     number;
  completedOnTime: number;
  completedLate:   number;
  currentDay:      number;
  totalDays:       number;
  healthStatus:    HealthStatus;
}

// ── Visual config per status ────────────────────────────────────────────────

const STATUS_CONFIG: Record<StoryExecutionStatus, {
  label: string;
  bg: string;
  color: string;
  borderColor: string;
  icon: string;
  pulse: boolean;
}> = {
  TODO: {
    label:       'Todo',
    bg:          'rgba(148,163,184,0.08)',
    color:       '#94a3b8',
    borderColor: '#94a3b8',
    icon:        '\u25CB',   // ○
    pulse:       false,
  },
  RUNNING: {
    label:       'Running',
    bg:          'rgba(124,58,237,0.08)',
    color:       '#7c3aed',
    borderColor: '#7c3aed',
    icon:        '\u25B6',   // ▶
    pulse:       true,
  },
  PR_READY: {
    label:       'PR Ready',
    bg:          'rgba(16,185,129,0.08)',
    color:       '#10b981',
    borderColor: '#10b981',
    icon:        '\u2713',   // ✓
    pulse:       false,
  },
  CI_FIXING: {
    label:       'CI Fixing',
    bg:          'rgba(245,158,11,0.08)',
    color:       '#f59e0b',
    borderColor: '#f59e0b',
    icon:        '\u26A0',   // ⚠
    pulse:       true,
  },
  BLOCKED: {
    label:       'Blocked',
    bg:          'rgba(239,68,68,0.08)',
    color:       '#ef4444',
    borderColor: '#ef4444',
    icon:        '\u2716',   // ✖
    pulse:       false,
  },
  DONE: {
    label:       'Done',
    bg:          'rgba(34,197,94,0.08)',
    color:       '#22c55e',
    borderColor: '#22c55e',
    icon:        '\u2714',   // ✔
    pulse:       false,
  },
};

const TIMING_CONFIG: Record<TimingStatus, {
  label: (daysLate: number) => string;
  bg: string;
  color: string;
  border: string;
}> = {
  ON_TRACK: {
    label:  () => 'ON TRACK',
    bg:     'rgba(34,197,94,0.10)',
    color:  '#22c55e',
    border: '#22c55e',
  },
  LATE: {
    label:  (d) => d > 0 ? `LATE \u00B7 ${d}d overdue` : 'LATE',
    bg:     'rgba(239,68,68,0.10)',
    color:  '#ef4444',
    border: '#ef4444',
  },
  RUNNING_LATE: {
    label:  () => 'RUNNING LATE',
    bg:     'rgba(245,158,11,0.10)',
    color:  '#f59e0b',
    border: '#f59e0b',
  },
  COMPLETED_ON_TIME: {
    label:  () => 'DONE ON TIME',
    bg:     'transparent',
    color:  '#22c55e',
    border: '#22c55e',
  },
  COMPLETED_LATE: {
    label:  (d) => d > 0 ? `DONE \u00B7 ${d}d late` : 'DONE LATE',
    bg:     'rgba(148,163,184,0.08)',
    color:  '#94a3b8',
    border: '#94a3b8',
  },
};

const PRIORITY_STYLE: Record<string, { bg: string; color: string }> = {
  P0: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
  P1: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  P2: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
};

const HEALTH_CONFIG: Record<HealthStatus, { label: string; bg: string; color: string }> = {
  GREEN: { label: 'ON TRACK',  bg: 'rgba(34,197,94,0.12)',  color: '#22c55e' },
  AMBER: { label: 'ATTENTION', bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  RED:   { label: 'DELAYED',   bg: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
};

// ── Timing badge helper ─────────────────────────────────────────────────────

// Exported for reuse — DASH.B2 (#824) MyAssignedWorkCard renders the same badge.
export function TimingBadge({ timing, daysLate }: { timing: TimingStatus; daysLate: number }) {
  const config = TIMING_CONFIG[timing];

  const style: CSSProperties = {
    display:      'inline-flex',
    alignItems:   'center',
    fontSize:     9,
    fontWeight:   700,
    padding:      '1px 6px',
    borderRadius: 9999,
    background:   config.bg,
    color:        config.color,
    border:       timing === 'COMPLETED_ON_TIME' ? `1px solid ${config.border}` : 'none',
    letterSpacing: 0.3,
    fontFamily:   'var(--font-body)',
  };

  return (
    <span style={style} data-testid="timing-badge">
      {config.label(daysLate)}
    </span>
  );
}

// ── StoryStatusCard ─────────────────────────────────────────────────────────

export type EstimationDisplay = 'STORY_POINTS' | 'EU_RU' | 'BOTH';

const EU_SIZE_MINUTES: Record<string, number> = { S: 12, M: 25, L: 50, XL: 90 };

function formatEstimate(
  storyPoints: number | null,
  euSize: string | null | undefined,
  estimationType: EstimationDisplay,
): string | null {
  const sp = storyPoints != null ? `${storyPoints}pts` : null;
  const euMin = euSize ? EU_SIZE_MINUTES[euSize] ?? null : null;
  const eu = euMin != null ? `EU:${euMin}` : null;

  switch (estimationType) {
    case 'STORY_POINTS': return sp;
    case 'EU_RU':        return eu;
    case 'BOTH':         return sp && eu ? `${sp} | ${eu}` : sp ?? eu;
    default:             return sp;
  }
}

export default function StoryStatusCard({
  story,
  estimationType = 'STORY_POINTS',
}: {
  story: StoryWithExecution;
  estimationType?: EstimationDisplay;
}) {
  const config = STATUS_CONFIG[story.executionStatus];

  const showTimingBadge = story.timingStatus != null
    && story.executionStatus !== 'BLOCKED'
    && story.executionStatus !== 'CI_FIXING';

  const cardStyle: CSSProperties = {
    background:   'var(--surface)',
    border:       '1px solid var(--border)',
    borderLeft:   `3px solid ${config.borderColor}`,
    borderRadius: 'var(--radius-md)',
    padding:      '10px 12px',
  };

  const badgeStyle: CSSProperties = {
    display:      'inline-flex',
    alignItems:   'center',
    gap:          4,
    background:   config.bg,
    color:        config.color,
    fontSize:     10,
    fontWeight:   700,
    padding:      '2px 8px',
    borderRadius: 4,
    fontFamily:   'var(--font-body)',
  };

  const pStyle = PRIORITY_STYLE[story.priority ?? ''];

  return (
    <div style={cardStyle} data-testid="story-status-card">
      {/* Top row: external ID + priority + timing badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-secondary)',
        }}>
          {story.externalId ?? story.id.slice(0, 8)}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {showTimingBadge && (
            <TimingBadge timing={story.timingStatus!} daysLate={story.daysLate ?? 0} />
          )}
          {pStyle && (
            <span style={{ ...pStyle, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
              {story.priority}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <p style={{
        fontFamily:       'var(--font-body)',
        fontSize:         13,
        fontWeight:       500,
        color:            'var(--text)',
        margin:           '0 0 8px',
        lineHeight:       1.4,
        display:          '-webkit-box',
        WebkitLineClamp:  2,
        WebkitBoxOrient:  'vertical',
        overflow:         'hidden',
      }}>
        {story.title}
      </p>

      {/* Status badge + story points row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={badgeStyle} data-testid="execution-badge">
          <span style={{
            animation: config.pulse ? 'pulse 1.5s ease-in-out infinite' : 'none',
          }}>
            {config.icon}
          </span>
          {config.label}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* PR link */}
          {story.prNumber && story.prUrl && (
            <a
              href={story.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                fontSize:   10,
                color:      'var(--primary)',
                textDecoration: 'none',
              }}
              data-testid="pr-link"
            >
              #{story.prNumber}
            </a>
          )}

          {/* Estimate badge */}
          {(() => {
            const label = formatEstimate(story.storyPoints, (story as unknown as Record<string, unknown>).euSize as string | null | undefined, estimationType);
            if (!label) return null;
            const isCircle = estimationType === 'STORY_POINTS' && story.storyPoints != null && !label.includes('|');
            return (
              <span
                data-testid="estimate-badge"
                style={isCircle ? {
                  background: 'var(--primary)', color: '#fff', borderRadius: '50%',
                  width: 20, height: 20, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                } : {
                  background: 'rgba(124,58,237,0.1)', color: 'var(--primary)',
                  borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                  flexShrink: 0, whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ── SprintSummaryHeader ─────────────────────────────────────────────────────

export function SprintSummaryHeader({
  summary,
  sprintName,
}: {
  summary: SprintSummary;
  sprintName?: string;
}) {
  const health = HEALTH_CONFIG[summary.healthStatus];

  const containerStyle: CSSProperties = {
    display:      'flex',
    alignItems:   'center',
    gap:          12,
    padding:      '8px 16px',
    background:   'var(--surface)',
    border:       '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    marginBottom: 16,
    fontFamily:   'var(--font-body)',
    fontSize:     13,
    flexWrap:     'wrap',
  };

  const healthBadgeStyle: CSSProperties = {
    display:      'inline-flex',
    alignItems:   'center',
    fontSize:     10,
    fontWeight:   700,
    padding:      '2px 8px',
    borderRadius: 9999,
    background:   health.bg,
    color:        health.color,
    letterSpacing: 0.3,
  };

  const separatorStyle: CSSProperties = {
    color:   'var(--text-secondary)',
    opacity: 0.4,
  };

  const done = summary.completedOnTime + summary.completedLate;

  return (
    <div style={containerStyle} data-testid="sprint-summary">
      <span style={{ fontWeight: 700 }}>
        {sprintName ?? 'Sprint'}
      </span>
      <span style={separatorStyle}>&middot;</span>
      <span style={{ color: 'var(--text-secondary)' }}>
        Day {summary.currentDay} of {summary.totalDays}
      </span>
      <span style={separatorStyle}>&middot;</span>
      <span style={healthBadgeStyle} data-testid="health-badge">
        {health.label}
      </span>
      <span style={separatorStyle}>&middot;</span>
      <span style={{ color: 'var(--text-secondary)' }}>
        {summary.onTrack} on track
        <span style={separatorStyle}> &middot; </span>
        {summary.late + summary.runningLate} late
        <span style={separatorStyle}> &middot; </span>
        {done} done
      </span>
    </div>
  );
}
